import { Menu } from './Consumable'
import { ProductLibrary } from './ProductLibrary'

export interface CourseItemSummary {
    productName: string
    quantity: number
    modifierNames: string[]
}

export interface CourseSummary {
    courseName: string
    items: CourseItemSummary[]
}

export interface PerCoverRow {
    name: string
    notes: string | null
    selectionsByCourse: Map<string, string[]>
}

export interface OrderDetailsData {
    courseNames: string[]
    courseSummaries: CourseSummary[]
    rows: PerCoverRow[]
}

export class CoverMenuChoice {
    constructor(
        public name: string | null,
        public notes: string | null,
        public selections: CoverProductSelection[]
    ) {}
}

export class CoverProductSelection {
    constructor(
        public productSkuId: string,
        public modifierSkuIds: string[],
        public menuItemId: string | null = null,
    ) {}
}

export class BookingDocket {
    constructor(
        public id: string,
        public menuId: string,
        public coverChoices: CoverMenuChoice[],
        public dateCreated: Date,
        public dateUpdated: Date,
        public minimumCoursesRequired: number | null = null,
        public maximumCoursesRequired: number | null = null
    ) {}

    public isComplete(partySize: number): boolean {
        if (partySize <= 0) {
            return true
        }
        if (!this.coverChoices || this.coverChoices.length < partySize) {
            return false
        }
    const withAtLeastOneChoice = this.coverChoices.filter(c => c.selections && c.selections.length > 0).length
        return withAtLeastOneChoice >= partySize
    }

    public completedCovers(partySize: number): number {
        const total = Math.max(partySize, 0)
    const withAtLeastOne = (this.coverChoices || []).filter(c => c.selections && c.selections.length > 0).length
        return Math.min(withAtLeastOne, total)
    }

    public progressPercent(partySize: number): number {
        const total = Math.max(partySize, 0)
        if (total === 0) { return 0 }
        return Math.round((this.completedCovers(total) / total) * 100)
    }

    public buildOrderDetails(menu: Menu, productLibrary: ProductLibrary): OrderDetailsData {
        const covers = this.coverChoices || []

        const courseMap = new Map<string, {
            courseName: string
            orderIndex: number
            itemMap: Map<string, { productName: string; quantity: number; modifierSet: Set<string> }>
        }>()

        for (const choice of covers) {
            for (const selection of choice.selections || []) {
                const skuId = selection.productSkuId
                const courseInfo = menu.findCourseForSelection(selection)
                const courseName = courseInfo?.displayName ?? 'Other'
                const courseOrderIndex = courseInfo?.orderIndex ?? 999

                if (!courseMap.has(courseName)) {
                    courseMap.set(courseName, {
                        courseName,
                        orderIndex: courseOrderIndex,
                        itemMap: new Map()
                    })
                }

                const course = courseMap.get(courseName)!
                const productName = productLibrary.resolveProductName(skuId, menu) ?? 'Item'

                if (!course.itemMap.has(skuId)) {
                    course.itemMap.set(skuId, {
                        productName,
                        quantity: 0,
                        modifierSet: new Set()
                    })
                }

                const item = course.itemMap.get(skuId)!
                item.quantity++

                for (const modId of selection.modifierSkuIds || []) {
                    const modName = productLibrary.resolveModifierName(modId)
                    if (modName) {
                        item.modifierSet.add(modName)
                    }
                }
            }
        }

        const sortedCourses = Array.from(courseMap.values())
            .sort((a, b) => a.orderIndex - b.orderIndex)

        const courseNames = sortedCourses.map(c => c.courseName)

        const courseSummaries: CourseSummary[] = sortedCourses.map(course => ({
            courseName: course.courseName,
            items: Array.from(course.itemMap.values()).map(item => ({
                productName: item.productName,
                quantity: item.quantity,
                modifierNames: Array.from(item.modifierSet)
            }))
        }))

        const rows: PerCoverRow[] = covers.map((choice, index) => {
            const selectionsByCourse = new Map<string, string[]>()

            for (const selection of choice.selections || []) {
                const skuId = selection.productSkuId
                const courseInfo = menu.findCourseForSelection(selection)
                const courseName = courseInfo?.displayName ?? 'Other'
                const productName = productLibrary.resolveProductName(skuId, menu) ?? 'Item'

                const modifierNames: string[] = []
                for (const modId of selection.modifierSkuIds || []) {
                    const modName = productLibrary.resolveModifierName(modId)
                    if (modName) {
                        modifierNames.push(modName)
                    }
                }
                const displayString = modifierNames.length > 0
                    ? `${productName} (${modifierNames.join(', ')})`
                    : productName

                if (!selectionsByCourse.has(courseName)) {
                    selectionsByCourse.set(courseName, [])
                }
                selectionsByCourse.get(courseName)!.push(displayString)
            }

            return {
                name: choice.name || `Guest ${index + 1}`,
                notes: choice.notes,
                selectionsByCourse
            }
        })

        return { courseNames, courseSummaries, rows }
    }
}
